import React, { useRef, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FileUpload } from 'primereact/fileupload';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Checkbox } from 'primereact/checkbox';
import { ProgressSpinner } from 'primereact/progressspinner';
import { validateFile, formatFileSize } from '../utils/fileValidation';
import { getAllRules } from '../../../service/dataCategorizationService';

/**
 * FileUploader Component
 *
 * File upload with drag & drop support, validation, and rule selection
 *
 * @component
 */
const FileUploader = ({ onFileSelect, onUpload, disabled = false, loading = false, selectedTeam = '' }) => {
  const fileUploadRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [teamRules, setTeamRules] = useState([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [selectedRuleIds, setSelectedRuleIds] = useState([]);

  /**
   * Fetch rules for the selected team
   */
  const fetchTeamRules = useCallback(async (team) => {
    if (!team) {
      setTeamRules([]);
      setSelectedRuleIds([]);
      return;
    }
    setRulesLoading(true);
    try {
      const response = await getAllRules({ team, enabled: true, page: 0, size: 1000 });
      const rules = response.data?.content || response.data || [];
      setTeamRules(rules);
      // Select all rules by default
      setSelectedRuleIds(rules.map(r => r.ruleId || r.id));
    } catch (err) {
      console.error('[FileUploader] Failed to fetch rules:', err);
      setTeamRules([]);
      setSelectedRuleIds([]);
    } finally {
      setRulesLoading(false);
    }
  }, []);

  // Fetch rules when team changes
  useEffect(() => {
    fetchTeamRules(selectedTeam);
  }, [selectedTeam, fetchTeamRules]);

  /**
   * Handle file selection
   */
  const handleFileSelect = (e) => {
    console.log('[FileUploader] File selected:', e);
    const file = e.files[0];

    if (!file) {
      console.log('[FileUploader] No file in event');
      setSelectedFile(null);
      setValidationErrors([]);
      onFileSelect(null);
      return;
    }

    console.log('[FileUploader] File details:', { name: file.name, size: file.size, type: file.type });

    // Validate file
    const validation = validateFile(file);
    console.log('[FileUploader] Validation result:', validation);

    if (validation.isValid) {
      setValidationErrors([]);
      setSelectedFile(file);
      onFileSelect(file);
      console.log('[FileUploader] File accepted:', file.name);
    } else {
      setValidationErrors(validation.errors);
      setSelectedFile(null);
      onFileSelect(null);
      console.log('[FileUploader] File rejected:', validation.errors);
      // Clear the file upload component
      if (fileUploadRef.current) {
        fileUploadRef.current.clear();
      }
    }
  };

  /**
   * Handle file clear
   */
  const handleClear = () => {
    setSelectedFile(null);
    setValidationErrors([]);
    onFileSelect(null);
  };

  /**
   * Custom item template for selected file
   */
  const itemTemplate = (file) => (
    <div className="file-item">
      <div className="file-icon">
        <i className="pi pi-file" style={{ fontSize: '2em', color: '#007bff' }}></i>
      </div>
      <div className="file-details">
        <div className="file-name">{file.name}</div>
        <div className="file-size">
          <Tag value={formatFileSize(file.size)} severity="info" />
        </div>
      </div>
      <Button
        icon="pi pi-times"
        className="p-button-rounded p-button-text p-button-danger"
        onClick={handleClear}
        disabled={disabled || loading}
      />
    </div>
  );

  /**
   * Empty template when no file selected
   */
  const emptyTemplate = () => (
    <div className="upload-empty">
      <i className="pi pi-cloud-upload" style={{ fontSize: '3em', color: '#0053A0' }}></i>
      <p className="upload-text">
        Drag and drop file here or click "Choose File"
      </p>
      <p className="upload-hint">
        Accepted formats: CSV, Excel (.csv, .xls, .xlsx)
      </p>
      <p className="upload-hint">
        Maximum file size: 100MB
      </p>
    </div>
  );

  /**
   * Custom header template
   */
  const headerTemplate = (options) => {
    const { chooseButton } = options;

    return (
      <div className="upload-header" style={{ 
        padding: '10px 20px', 
        borderBottom: '1px solid #dee2e6',
        background: 'white'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ 
            fontWeight: 600, 
            color: '#343A40',
            fontSize: '14px'
          }}>
            📁 Select File
          </span>
          {chooseButton}
        </div>
      </div>
    );
  };

  return (
    <div className="file-uploader-container">
      {disabled ? (
        <div className="p-fileupload p-fileupload-advanced p-disabled" style={{ border: '2px dashed #dee2e6', borderRadius: '4px', background: '#f8f9fa' }}>
          <div className="upload-header" style={{ 
            padding: '10px 20px', 
            borderBottom: '1px solid #dee2e6',
            background: '#f8f9fa'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 600, color: '#6c757d', fontSize: '14px' }}>
                🔒 Team Selection Required
              </span>
              <Button 
                label="Choose File" 
                icon="pi pi-folder-open"
                disabled={true}
                className="p-button-primary"
                style={{ opacity: 0.6 }}
              />
            </div>
          </div>
          <div style={{ padding: '40px 20px', textAlign: 'center' }}>
            <i className="pi pi-lock" style={{ fontSize: '3em', color: '#ffc107', marginBottom: '16px', display: 'block' }}></i>
            <p style={{ color: '#dc3545', fontWeight: 'bold', fontSize: '16px', marginBottom: '10px' }}>
              ⚠️ Please select a team first
            </p>
            <p style={{ color: '#6c757d', fontSize: '13px' }}>
              Select a team from the dropdown above to enable file upload
            </p>
          </div>
        </div>
      ) : (
        <FileUpload
          ref={fileUploadRef}
          mode="advanced"
          name="file"
          accept=".csv,.xls,.xlsx"
          maxFileSize={104857600}
          onSelect={handleFileSelect}
          onClear={handleClear}
          headerTemplate={headerTemplate}
          itemTemplate={itemTemplate}
          emptyTemplate={emptyTemplate}
          chooseLabel="Choose File"
          uploadLabel="Upload"
          cancelLabel="Cancel"
          disabled={loading}
          auto={false}
          customUpload
          multiple={false}
          chooseOptions={{
            icon: 'pi pi-folder-open',
            iconOnly: false,
            className: 'custom-choose-btn'
          }}
        />
      )}

      {validationErrors.length > 0 && (
        <div className="validation-errors">
          {validationErrors.map((error) => (
            <small key={error} className="p-error">
              <i className="pi pi-times-circle" style={{ marginRight: '5px' }}></i>
              {error}
            </small>
          ))}
        </div>
      )}

      {/* Rule selection section - shown after file is selected and valid */}
      {selectedFile && validationErrors.length === 0 && (
        <div className="rule-selection-section" style={{
          marginTop: '1rem',
          padding: '16px',
          border: '1px solid #dee2e6',
          borderRadius: '8px',
          background: '#f8f9fa'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '12px'
          }}>
            <span style={{ fontWeight: 600, fontSize: '14px', color: '#343A40' }}>
              📋 Select Rules to Apply
            </span>
            {teamRules.length > 0 && (
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button
                  label="Select All"
                  className="p-button-text p-button-sm"
                  onClick={() => setSelectedRuleIds(teamRules.map(r => r.ruleId || r.id))}
                  disabled={loading}
                  style={{ fontSize: '12px', padding: '4px 8px' }}
                />
                <Button
                  label="Deselect All"
                  className="p-button-text p-button-sm"
                  onClick={() => setSelectedRuleIds([])}
                  disabled={loading}
                  style={{ fontSize: '12px', padding: '4px 8px' }}
                />
              </div>
            )}
          </div>

          {rulesLoading && (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <ProgressSpinner style={{ width: '30px', height: '30px' }} />
              <p style={{ fontSize: '13px', color: '#6c757d', marginTop: '8px' }}>Loading rules...</p>
            </div>
          )}

          {!rulesLoading && teamRules.length === 0 && (
            <div style={{ textAlign: 'center', padding: '16px', color: '#6c757d', fontSize: '13px' }}>
              <i className="pi pi-info-circle" style={{ marginRight: '6px' }} />{' '}
              No enabled rules found for this team. All data will be processed without categorization rules.
            </div>
          )}

          {!rulesLoading && teamRules.length > 0 && (
            <div style={{
              maxHeight: '250px',
              overflowY: 'auto',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
              gap: '8px'
            }}>
              {teamRules.map((rule) => {
                const ruleId = rule.ruleId || rule.id;
                const isChecked = selectedRuleIds.includes(ruleId);
                const toggleRule = () => {
                  if (loading) return;
                  setSelectedRuleIds(prev =>
                    prev.includes(ruleId)
                      ? prev.filter(id => id !== ruleId)
                      : [...prev, ruleId]
                  );
                };
                return (
                  <div
                    key={ruleId}
                    role="checkbox"
                    aria-checked={isChecked}
                    tabIndex={0}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 12px',
                      background: 'white',
                      borderRadius: '6px',
                      border: isChecked ? '1px solid #0053A0' : '1px solid #dee2e6',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onClick={toggleRule}
                    onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); toggleRule(); } }}
                  >
                    <Checkbox
                      inputId={`rule-${ruleId}`}
                      checked={isChecked}
                      onChange={(e) => { e.stopPropagation(); toggleRule(); }}
                      disabled={loading}
                    />
                    <label
                      style={{
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: 500,
                        color: '#343A40',
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                      title={rule.name || rule.ruleName}
                    >
                      {rule.name || rule.ruleName}
                    </label>
                    {rule.targetCategory && (
                      <Tag
                        value={rule.targetCategory}
                        severity="info"
                        style={{ fontSize: '10px', padding: '2px 6px' }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {teamRules.length > 0 && (
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#6c757d' }}>
              {selectedRuleIds.length} of {teamRules.length} rule(s) selected
            </div>
          )}
        </div>
      )}

      {selectedFile && validationErrors.length === 0 && (
        <div className="upload-actions">
          <Button
            label="Upload and Process"
            icon="pi pi-upload"
            onClick={() => onUpload(selectedFile, selectedRuleIds)}
            disabled={loading || (teamRules.length > 0 && selectedRuleIds.length === 0)}
            loading={loading}
            className="p-button-primary upload-button"
          />
          {teamRules.length > 0 && selectedRuleIds.length === 0 && (
            <small className="p-error" style={{ display: 'block', marginTop: '6px' }}>
              <i className="pi pi-exclamation-triangle" style={{ marginRight: '4px' }}></i>
              Please select at least one rule to process the file.
            </small>
          )}
        </div>
      )}
    </div>
  );
};

FileUploader.propTypes = {
  onFileSelect: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  selectedTeam: PropTypes.string
};

export default FileUploader;

